import React, { useEffect, useState, useRef } from 'react';
import { X, Upload, Trash2, FileText, MessageSquare, HelpCircle, Plus, File, Tag } from 'lucide-react';
import { AppModal } from '../base/AppModal';
import { AppInput } from '../base/AppInput';
import { AppTextarea } from '../base/AppTextarea';
import { AppButton } from '../base/AppButton';
import { AppBadge } from '../base/AppBadge';
import type { KnowledgeSource, KnowledgeSourceType, KnowledgeSourceStatus } from '../../types';
// ============================================================================
// SOURCE TYPE SELECTION MODAL
// ============================================================================
interface SourceTypeSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectType: (type: 'documents' | 'prompt' | 'qa') => void;
}
export function SourceTypeSelectionModal({
  isOpen,
  onClose,
  onSelectType
}: SourceTypeSelectionModalProps) {
  return <AppModal isOpen={isOpen} onClose={onClose} title="Scegli il tipo di fonte" size="lg" data-id="element-334">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-335">
        <button onClick={() => onSelectType('documents')} className="flex flex-col items-center p-6 border border-gray-200 dark:border-dark-subtle rounded-xl hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all group text-center" data-id="element-336">
          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4 group-hover:scale-110 transition-transform" data-id="element-337">
            <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-338" />
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2" data-id="element-339">
            Documenti
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-340">
            Carica PDF, DOCX, TXT o altri file di documentazione.
          </p>
        </button>

        <button onClick={() => onSelectType('prompt')} className="flex flex-col items-center p-6 border border-gray-200 dark:border-dark-subtle rounded-xl hover:border-purple-500 dark:hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-all group text-center" data-id="element-341">
          <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full mb-4 group-hover:scale-110 transition-transform" data-id="element-342">
            <MessageSquare className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-343" />
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2" data-id="element-344">
            Istruzioni Mirate
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-345">
            Inserisci testo libero o istruzioni specifiche come fonte.
          </p>
        </button>

        <button onClick={() => onSelectType('qa')} className="flex flex-col items-center p-6 border border-gray-200 dark:border-dark-subtle rounded-xl hover:border-amber-500 dark:hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-all group text-center" data-id="element-346">
          <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-full mb-4 group-hover:scale-110 transition-transform" data-id="element-347">
            <HelpCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" data-id="element-348" />
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2" data-id="element-349">
            Domande & Risposte
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-350">
            Crea coppie di domande e risposte frequenti (FAQ).
          </p>
        </button>
      </div>
    </AppModal>;
}
// ============================================================================
// ADD DOCUMENT SOURCE MODAL
// ============================================================================
interface AddDocumentSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: Omit<KnowledgeSource, 'id'>) => Promise<void>;
}
export function AddDocumentSourceModal({
  isOpen,
  onClose,
  onCreate
}: AddDocumentSourceModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files || [])]);
    }
  };
  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };
  const handleCreate = async () => {
    setIsCreating(true);
    try {
      // In a real app, we would upload files here.
      // For mock, we'll just create the source with sample documents based on the files.
      const sampleDocs = files.map((f, i) => ({
        id: `doc-${Date.now()}-${i}`,
        title: f.name,
        snippet: 'Documento caricato...',
        tags: ['Upload']
      }));
      await onCreate({
        name,
        type: 'Documenti',
        source: 'Upload Manuale',
        status: 'Attivo',
        description,
        documentsCount: files.length,
        lastUpdated: new Date().toLocaleDateString('it-IT'),
        sampleDocuments: sampleDocs,
        tags: []
      });
      // Reset
      setName('');
      setDescription('');
      setFiles([]);
      onClose();
    } catch (error) {
      console.error('Failed to create document source', error);
    } finally {
      setIsCreating(false);
    }
  };
  return <AppModal isOpen={isOpen} onClose={onClose} title="Aggiungi Documenti" data-id="element-351">
      <div className="space-y-4" data-id="element-352">
        <AppInput label="Nome Fonte" value={name} onChange={e => setName(e.target.value)} placeholder="Es. Manuali Tecnici 2024" required data-id="element-353" />

        <AppTextarea label="Descrizione" value={description} onChange={e => setDescription(e.target.value)} placeholder="Descrivi il contenuto di questi documenti..." rows={2} data-id="element-354" />

        <div data-id="element-355">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-356">
            File
          </label>
          <div className="border-2 border-dashed border-gray-300 dark:border-dark-medium rounded-xl p-6 text-center hover:border-blue-500 dark:hover:border-blue-500 transition-colors cursor-pointer bg-gray-50 dark:bg-dark-subtle/50" onClick={() => fileInputRef.current?.click()} data-id="element-357">
            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" data-id="element-358" />
            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium" data-id="element-359">
              Clicca per caricare o trascina i file qui
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1" data-id="element-360">
              PDF, DOCX, TXT supportati
            </p>
            <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" multiple data-id="element-361" />
          </div>
        </div>

        {files.length > 0 && <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar" data-id="element-362">
            {files.map((file, index) => <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-dark-subtle rounded-lg border border-gray-100 dark:border-dark-medium" data-id="element-363">
                <div className="flex items-center gap-2 overflow-hidden" data-id="element-364">
                  <File className="w-4 h-4 text-blue-500 flex-shrink-0" data-id="element-365" />
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate" data-id="element-366">
                    {file.name}
                  </span>
                  <span className="text-xs text-gray-400" data-id="element-367">
                    ({(file.size / 1024).toFixed(0)} KB)
                  </span>
                </div>
                <button onClick={() => removeFile(index)} className="p-1 text-gray-400 hover:text-red-500 transition-colors" data-id="element-368">
                  <X className="w-4 h-4" data-id="element-369" />
                </button>
              </div>)}
          </div>}

        <div className="flex gap-3 pt-4" data-id="element-370">
          <AppButton variant="secondary" onClick={onClose} fullWidth data-id="element-371">
            Annulla
          </AppButton>
          <AppButton onClick={handleCreate} fullWidth disabled={isCreating || !name || files.length === 0} data-id="element-372">
            {isCreating ? 'Caricamento...' : 'Crea Fonte'}
          </AppButton>
        </div>
      </div>
    </AppModal>;
}
// ============================================================================
// ADD PROMPT SOURCE MODAL
// ============================================================================
interface AddPromptSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: Omit<KnowledgeSource, 'id'>) => Promise<void>;
}
export function AddPromptSourceModal({
  isOpen,
  onClose,
  onCreate
}: AddPromptSourceModalProps) {
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const handleCreate = async () => {
    setIsCreating(true);
    try {
      await onCreate({
        name,
        type: 'Corso',
        source: 'Istruzioni Mirate',
        status: 'Attivo',
        description,
        documentsCount: 1,
        lastUpdated: new Date().toLocaleDateString('it-IT'),
        sampleDocuments: [{
          id: `prompt-${Date.now()}`,
          title: 'Istruzioni',
          snippet: content.substring(0, 100) + '...',
          tags: ['Prompt']
        }],
        tags: []
      });
      setName('');
      setContent('');
      setDescription('');
      onClose();
    } catch (error) {
      console.error('Failed to create prompt source', error);
    } finally {
      setIsCreating(false);
    }
  };
  return <AppModal isOpen={isOpen} onClose={onClose} title="Aggiungi Istruzioni Mirate" data-id="element-373">
      <div className="space-y-4" data-id="element-374">
        <AppInput label="Nome Fonte" value={name} onChange={e => setName(e.target.value)} placeholder="Es. Regole di Tone of Voice" required data-id="element-375" />

        <AppTextarea label="Descrizione" value={description} onChange={e => setDescription(e.target.value)} placeholder="A cosa servono queste istruzioni..." rows={2} data-id="element-376" />

        <div data-id="element-377">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-378">
            Contenuto Istruzioni
          </label>
          <textarea value={content} onChange={e => setContent(e.target.value)} className="w-full h-48 p-3 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none font-mono" placeholder="Inserisci qui il testo delle istruzioni o del prompt..." data-id="element-379" />
          <div className="text-right text-xs text-gray-400 mt-1" data-id="element-380">
            {content.length} caratteri
          </div>
        </div>

        <div className="flex gap-3 pt-4" data-id="element-381">
          <AppButton variant="secondary" onClick={onClose} fullWidth data-id="element-382">
            Annulla
          </AppButton>
          <AppButton onClick={handleCreate} fullWidth disabled={isCreating || !name || !content} data-id="element-383">
            {isCreating ? 'Salvataggio...' : 'Crea Fonte'}
          </AppButton>
        </div>
      </div>
    </AppModal>;
}
// ============================================================================
// ADD Q&A SOURCE MODAL
// ============================================================================
interface AddQASourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: Omit<KnowledgeSource, 'id'>) => Promise<void>;
}
export function AddQASourceModal({
  isOpen,
  onClose,
  onCreate
}: AddQASourceModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [qaPairs, setQaPairs] = useState<Array<{
    question: string;
    answer: string;
  }>>([{
    question: '',
    answer: ''
  }]);
  const [isCreating, setIsCreating] = useState(false);
  const addPair = () => {
    setQaPairs([...qaPairs, {
      question: '',
      answer: ''
    }]);
  };
  const removePair = (index: number) => {
    if (qaPairs.length > 1) {
      setQaPairs(qaPairs.filter((_, i) => i !== index));
    }
  };
  const updatePair = (index: number, field: 'question' | 'answer', value: string) => {
    const newPairs = [...qaPairs];
    newPairs[index][field] = value;
    setQaPairs(newPairs);
  };
  const handleCreate = async () => {
    setIsCreating(true);
    try {
      // Filter out empty pairs
      const validPairs = qaPairs.filter(p => p.question.trim() && p.answer.trim());
      if (validPairs.length === 0) {
        // Show error or just return
        setIsCreating(false);
        return;
      }
      await onCreate({
        name,
        type: 'FAQ',
        source: 'Q&A Manuale',
        status: 'Attivo',
        description,
        documentsCount: validPairs.length,
        lastUpdated: new Date().toLocaleDateString('it-IT'),
        sampleDocuments: validPairs.map((p, i) => ({
          id: `qa-${Date.now()}-${i}`,
          title: p.question,
          snippet: p.answer,
          tags: ['FAQ']
        })),
        tags: []
      });
      setName('');
      setDescription('');
      setQaPairs([{
        question: '',
        answer: ''
      }]);
      onClose();
    } catch (error) {
      console.error('Failed to create Q&A source', error);
    } finally {
      setIsCreating(false);
    }
  };
  return <AppModal isOpen={isOpen} onClose={onClose} title="Aggiungi Domande & Risposte" size="lg" data-id="element-384">
      <div className="space-y-4" data-id="element-385">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-386">
          <AppInput label="Nome Fonte" value={name} onChange={e => setName(e.target.value)} placeholder="Es. FAQ Servizio Clienti" required data-id="element-387" />
          <AppInput label="Descrizione (opzionale)" value={description} onChange={e => setDescription(e.target.value)} placeholder="Breve descrizione..." data-id="element-388" />
        </div>

        <div className="border-t border-gray-100 dark:border-dark-subtle pt-4" data-id="element-389">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-390">
            Coppie Domanda / Risposta
          </label>

          <div className="space-y-4 max-h-[400px] overflow-y-auto custom-scrollbar pr-2" data-id="element-391">
            {qaPairs.map((pair, index) => <div key={index} className="p-4 bg-gray-50 dark:bg-dark-subtle/50 rounded-xl border border-gray-200 dark:border-dark-medium relative group" data-id="element-392">
                {qaPairs.length > 1 && <button onClick={() => removePair(index)} className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors opacity-0 group-hover:opacity-100" title="Rimuovi coppia" data-id="element-393">
                    <Trash2 className="w-4 h-4" data-id="element-394" />
                  </button>}

                <div className="space-y-3" data-id="element-395">
                  <div data-id="element-396">
                    <input type="text" value={pair.question} onChange={e => updatePair(index, 'question', e.target.value)} placeholder="Domanda..." className="w-full bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none font-medium" data-id="element-397" />
                  </div>
                  <div data-id="element-398">
                    <textarea value={pair.answer} onChange={e => updatePair(index, 'answer', e.target.value)} placeholder="Risposta..." rows={2} className="w-full bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none resize-none" data-id="element-399" />
                  </div>
                </div>
              </div>)}
          </div>

          <button onClick={addPair} className="mt-3 flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors" data-id="element-400">
            <Plus className="w-4 h-4" data-id="element-401" />
            Aggiungi altra coppia
          </button>
        </div>

        <div className="flex gap-3 pt-4 border-t border-gray-100 dark:border-dark-subtle mt-4" data-id="element-402">
          <AppButton variant="secondary" onClick={onClose} fullWidth data-id="element-403">
            Annulla
          </AppButton>
          <AppButton onClick={handleCreate} fullWidth disabled={isCreating || !name || !qaPairs[0].question} data-id="element-404">
            {isCreating ? 'Salvataggio...' : 'Crea FAQ'}
          </AppButton>
        </div>
      </div>
    </AppModal>;
}
// ============================================================================
// MANAGE KNOWLEDGE SOURCE MODAL
// ============================================================================
interface ManageKnowledgeSourceModalProps {
  isOpen: boolean;
  source: KnowledgeSource | null;
  onClose: () => void;
  onSave: (id: string, data: Partial<KnowledgeSource>) => Promise<void>;
  onAddFile: (sourceId: string, file: {
    name: string;
    size: number;
  }) => Promise<void>;
  onRemoveFile: (sourceId: string, fileId: string) => Promise<void>;
}
export function ManageKnowledgeSourceModal({
  isOpen,
  source,
  onClose,
  onSave,
  onAddFile,
  onRemoveFile
}: ManageKnowledgeSourceModalProps) {
  const [name, setName] = useState(source?.name || '');
  const [type, setType] = useState<KnowledgeSourceType>(source?.type || 'Documenti');
  const [sourceOrigin, setSourceOrigin] = useState(source?.source || '');
  const [status, setStatus] = useState<KnowledgeSourceStatus>(source?.status || 'Attivo');
  const [description, setDescription] = useState(source?.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  useEffect(() => {
    if (source) {
      setName(source.name);
      setType(source.type);
      setSourceOrigin(source.source);
      setStatus(source.status);
      setDescription(source.description);
    }
  }, [source]);
  const handleSave = async () => {
    if (!source) return;
    setIsSaving(true);
    try {
      await onSave(source.id, {
        name,
        type,
        source: sourceOrigin,
        status,
        description
      });
      onClose();
    } catch (error) {
      console.error('Failed to save knowledge source', error);
    } finally {
      setIsSaving(false);
    }
  };
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!source || !e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setIsUploadingFile(true);
    try {
      await onAddFile(source.id, {
        name: file.name,
        size: file.size
      });
    } catch (error) {
      console.error('Failed to upload file', error);
    } finally {
      setIsUploadingFile(false);
      e.target.value = ''; // Reset input
    }
  };
  return <AppModal isOpen={isOpen} onClose={onClose} title="Gestisci Fonte di Conoscenza" data-id="element-405">
      <div className="space-y-6" data-id="element-406">
        {/* Basic Info */}
        <div className="space-y-4" data-id="element-407">
          <AppInput label="Nome" value={name} onChange={e => setName(e.target.value)} placeholder="Nome della fonte" data-id="element-408" />

          <div data-id="element-409">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-410">
              Tipo
            </label>
            <select value={type} onChange={e => setType(e.target.value as KnowledgeSourceType)} className="w-full bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle text-gray-700 dark:text-gray-300 py-2.5 px-3.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" data-id="element-411">
              <option value="Corso" data-id="element-412">Corso</option>
              <option value="FAQ" data-id="element-413">FAQ</option>
              <option value="Profili utenti" data-id="element-414">Profili utenti</option>
              <option value="Eventi" data-id="element-415">Eventi</option>
              <option value="Documenti" data-id="element-416">Documenti</option>
            </select>
          </div>

          <AppInput label="Origine" value={sourceOrigin} onChange={e => setSourceOrigin(e.target.value)} placeholder="Origine dei dati" data-id="element-417" />

          <div data-id="element-418">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-419">
              Stato
            </label>
            <select value={status} onChange={e => setStatus(e.target.value as KnowledgeSourceStatus)} className="w-full bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle text-gray-700 dark:text-gray-300 py-2.5 px-3.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" data-id="element-420">
              <option value="Attivo" data-id="element-421">Attivo</option>
              <option value="In aggiornamento" data-id="element-422">In aggiornamento</option>
              <option value="Disattivato" data-id="element-423">Disattivato</option>
            </select>
          </div>

          <AppTextarea label="Descrizione" value={description} onChange={e => setDescription(e.target.value)} placeholder="Descrizione della fonte" rows={3} data-id="element-424" />
        </div>

        {/* File Management */}
        <div className="border-t border-gray-200 dark:border-dark-subtle pt-6" data-id="element-425">
          <div className="flex items-center justify-between mb-4" data-id="element-426">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100" data-id="element-427">
              Documenti ({source?.documentsCount || 0})
            </h4>
            <label className="cursor-pointer" data-id="element-428">
              <input type="file" className="hidden" onChange={handleFileUpload} disabled={isUploadingFile} data-id="element-429" />
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-sm font-medium" data-id="element-430">
                <Upload className="w-4 h-4" data-id="element-431" />
                {isUploadingFile ? 'Caricamento...' : 'Aggiungi file'}
              </div>
            </label>
          </div>

          {source?.sampleDocuments && source.sampleDocuments.length > 0 && <div className="space-y-2" data-id="element-432">
              {source.sampleDocuments.map(doc => <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-subtle rounded-lg" data-id="element-433">
                  <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-434">
                    <FileText className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" data-id="element-435" />
                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate" data-id="element-436">
                      {doc.title}
                    </span>
                  </div>
                  <button onClick={() => onRemoveFile(source.id, doc.id)} className="p-1.5 text-gray-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded transition-colors" data-id="element-437">
                    <Trash2 className="w-4 h-4" data-id="element-438" />
                  </button>
                </div>)}
            </div>}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4" data-id="element-439">
          <AppButton variant="secondary" onClick={onClose} fullWidth data-id="element-440">
            Annulla
          </AppButton>
          <AppButton onClick={handleSave} fullWidth disabled={isSaving || !name || !sourceOrigin} data-id="element-441">
            {isSaving ? 'Salvataggio...' : 'Salva modifiche'}
          </AppButton>
        </div>
      </div>
    </AppModal>;
}
// ============================================================================
// VIEW SOURCE DETAIL MODAL
// ============================================================================
interface ViewSourceDetailModalProps {
  isOpen: boolean;
  source: KnowledgeSource | null;
  onClose: () => void;
  onManage: (source: KnowledgeSource) => void;
  getStatusBadgeVariant: (status: KnowledgeSourceStatus) => 'green' | 'amber' | 'gray';
}
export function ViewSourceDetailModal({
  isOpen,
  source,
  onClose,
  onManage,
  getStatusBadgeVariant
}: ViewSourceDetailModalProps) {
  if (!source) return null;
  return <AppModal isOpen={isOpen} onClose={onClose} title={source.name} data-id="element-442">
      <div className="space-y-6" data-id="element-443">
        {/* Basic Info */}
        <div className="space-y-5" data-id="element-444">
          <div data-id="element-445">
            <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-446">
              Tipo
            </p>
            <p className="text-sm text-gray-900 dark:text-gray-100 font-medium" data-id="element-447">
              {source.type}
            </p>
          </div>

          <div data-id="element-448">
            <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-449">
              Origine
            </p>
            <p className="text-sm text-gray-900 dark:text-gray-100 font-medium" data-id="element-450">
              {source.source}
            </p>
          </div>

          <div data-id="element-451">
            <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-452">
              Stato
            </p>
            <AppBadge variant={getStatusBadgeVariant(source.status)} data-id="element-453">
              {source.status}
            </AppBadge>
          </div>

          <div data-id="element-454">
            <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-455">
              Descrizione
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed" data-id="element-456">
              {source.description}
            </p>
          </div>
        </div>

        {/* Sample Documents */}
        <div className="border-t border-gray-100 dark:border-dark-subtle pt-6" data-id="element-457">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4" data-id="element-458">
            Documenti di esempio
          </h4>
          <div className="space-y-3" data-id="element-459">
            {source.sampleDocuments.map(doc => <div key={doc.id} className="bg-gray-50 dark:bg-dark-subtle rounded-xl p-3 border border-gray-100 dark:border-dark-medium hover:border-gray-200 dark:hover:border-gray-600 transition-colors" data-id="element-460">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1" data-id="element-461">
                  {doc.title}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-2" data-id="element-462">
                  {doc.snippet}
                </p>
                <div className="flex flex-wrap gap-1" data-id="element-463">
                  {doc.tags.map(tag => <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-white dark:bg-dark-surface text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-dark-medium" data-id="element-464">
                      <Tag className="w-3 h-3 mr-1 opacity-70" data-id="element-465" />
                      {tag}
                    </span>)}
                </div>
              </div>)}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-100 dark:border-dark-subtle" data-id="element-466">
          <AppButton variant="secondary" onClick={onClose} fullWidth data-id="element-467">
            Chiudi
          </AppButton>
          <AppButton onClick={() => onManage(source)} fullWidth data-id="element-468">
            Gestisci contenuti
          </AppButton>
        </div>
      </div>
    </AppModal>;
}